import { Grid, Typography } from '@mui/material';
import { styled } from '@mui/styles';
import type { composeCoverageDeltasByGroupNames } from '../helpers/coverage-summary.utils';
import { DetailsCard } from '../CoverageSummary';
import { CoverageLegend } from './CoverageLegend';
import { CoverageRow } from './CoverageSummaryCoverages/CoverageRow';
import { CoverageHeader } from './CoverageSummaryCoverages/CoverageHeader';
import { Show } from '../../../../../../../components/Show';

interface CoverageSummaryCoveragesProps {
  hasCoverageDelta: boolean;
  mergedCoveragesByGroups: ReturnType<typeof composeCoverageDeltasByGroupNames>;
  showAllDetails: boolean;
}

export const CoverageSummaryCoverages = ({
  hasCoverageDelta,
  mergedCoveragesByGroups,
  showAllDetails,
}: CoverageSummaryCoveragesProps) => {
  return (
    <DetailsCard sx={{ marginTop: '1rem' }}>
      <Grid container p={0} rowSpacing={0}>
        <Grid item sm={12} sx={{ margin: '1rem' }}>
          <HeaderTypography variant="subtitle2" color="primary">
            Coverage Details
          </HeaderTypography>
        </Grid>

        <Show when={hasCoverageDelta || showAllDetails}>
          <Grid item sm={12} sx={{ margin: '1rem' }}>
            <CoverageLegend />
          </Grid>

          <Grid item sm={4} sx={{ marginBottom: '1rem' }}>
            <HeaderTypography variant="caption"> </HeaderTypography>
          </Grid>

          <Grid item sm={4} sx={{ marginBottom: '1rem', textAlign: 'center' }}>
            <HeaderTypography variant="caption" align="center">
              Policy
            </HeaderTypography>
          </Grid>

          <Grid item sm={4} sx={{ marginBottom: '1rem', textAlign: 'center' }}>
            <HeaderTypography variant="caption">Endorsement</HeaderTypography>
          </Grid>
        </Show>

        <Show when={!hasCoverageDelta && !showAllDetails}>
          <Grid item sm={12} sx={{ textAlign: 'center' }}>
            There are no difference in coverages
          </Grid>
        </Show>
      </Grid>

      {Object.keys(mergedCoveragesByGroups).map((coverageGroup) => {
        return (
          <Grid container sx={{ marginBottom: '1rem' }} key={coverageGroup}>
            <Show
              when={shouldShowCoverageGroup(
                mergedCoveragesByGroups[coverageGroup],
                showAllDetails
              )}
            >
              <CoverageHeader coverageGroup={coverageGroup} />
            </Show>

            {mergedCoveragesByGroups[coverageGroup].map(
              (mergedCoverage, index) => {
                if (!showAllDetails && !mergedCoverage.isDelta) {
                  return null;
                }

                const covName = (mergedCoverage.endorsement?.name ??
                  mergedCoverage.policy?.name) as string;

                return (
                  <CoverageRow
                    key={covName}
                    mergedCoverage={mergedCoverage}
                    isOdd={index % 2 !== 0}
                  />
                );
              }
            )}
          </Grid>
        );
      })}
    </DetailsCard>
  );
};

const shouldShowCoverageGroup = (coverages: any, showAllDetails: boolean) => {
  if (!showAllDetails) {
    return coverages.filter((coverage: any) => coverage.isDelta).length > 0;
  }

  return true;
};

const HeaderTypography = styled(Typography)(() => {
  return {
    textTransform: 'uppercase',
  };
});
